import { useEffect, useRef, useState } from 'react';
import { useContext } from 'react';
import { AppConfig } from '@ltvco/refresh-lib/ctx';

interface UseIframeManagerProps {
  iframeSrc: any;
  paymentMethod: string;
  subscribeErrors: string[];
  onError: (message: string) => void;
}

export const useIframeManager = ({
  iframeSrc,
  paymentMethod,
  subscribeErrors,
  onError,
}: UseIframeManagerProps) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [tokenExIframe, setTokenExIframe] = useState<any | null>(null);
  const [iframeRetryCount, setIframeRetryCount] = useState(0);
  const iframeLoadFailedRef = useRef(false);
  const { logError } = useContext(AppConfig);

  const MAX_IFRAME_RETRIES = 3;

  // Main iframe management effect
  useEffect(() => {
    // Only process iframe for card payments
    if (paymentMethod !== 'card') {
      return;
    }

    // Don't retry if iframe has already failed
    if (iframeLoadFailedRef.current) {
      return;
    }

    // Don't process if there are existing errors
    if (subscribeErrors.length > 0) {
      return;
    }

    // Check retry limit
    if (iframeRetryCount >= MAX_IFRAME_RETRIES) {
      logError(
        'Max iframe retries reached',
        new Error(
          'Max iframe retries reached - disabling iframe to prevent infinite loading'
        )
      );
      setIframeLoaded(true);
      iframeLoadFailedRef.current = true;
      return;
    }

    // Initialize iframe if we have valid source and no existing iframe
    if (
      iframeSrc &&
      !tokenExIframe &&
      typeof iframeSrc === 'object' &&
      iframeSrc !== null
    ) {
      try {
        if (typeof iframeSrc.load === 'function') {
          setTokenExIframe(iframeSrc);
          iframeSrc.load();
        } else {
          // Invalid iframe source - increment retry count
          logError(
            'Invalid iframe source',
            new Error(
              `iframeSrc.load is not a function: ${JSON.stringify(iframeSrc)}`
            )
          );
          setIframeLoaded(false);
          setIframeRetryCount((prev) => prev + 1);
        }
      } catch (error) {
        // Error during iframe initialization - increment retry count
        logError('Failed to initialize TokenEx iframe', error as Error);
        setIframeLoaded(false);
        setIframeRetryCount((prev) => prev + 1);
      }
    } else if (!iframeSrc) {
      // No iframe source available yet
      setIframeLoaded(false);
    }

    // Cleanup: only remove iframe, don't modify state
    return () => {
      if (tokenExIframe && typeof tokenExIframe.remove === 'function') {
        try {
          tokenExIframe.remove();
        } catch (error) {
          logError('Error removing TokenEx iframe', error as Error);
        }
      }
    };
  }, [
    iframeSrc,
    tokenExIframe,
    paymentMethod,
    subscribeErrors,
    iframeRetryCount,
    logError,
    onError,
  ]);

  // Reset iframe state when payment method changes
  const resetIframeState = () => {
    setIframeLoaded(false);
    setTokenExIframe(null);
    setIframeRetryCount(0);
    iframeLoadFailedRef.current = false;
  };

  // Reset retry count when iframe source changes
  useEffect(() => {
    setIframeRetryCount(0);
  }, [iframeSrc]);

  return {
    iframeLoaded,
    tokenExIframe,
    iframeRetryCount,
    resetIframeState,
  };
};
